import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  CustomAutocomplete,
  CustomSelect,
  DropDownItem,
  filterOptionsDropDownWithAll,
  getIndexValue,
  InputLabel,
  ListboxComponent,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Pill,
  renderDropDownOptionWithAll,
  renderDropDownTags,
  SearchBar,
  SectionHeader,
  stringToLongDateAndShortTime,
  TableActionTypes,
  TableState,
  Typography,
  UPDATE_DATA,
  UPDATE_FILTERS,
  UPDATE_SEARCH,
  MUIDataTableColumn
} from 'web-library';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { useGetExamLevel } from '../../../../api-refactored/exam-levels/useGetExamLevel';
import {
  JobStatusReportsContent,
  JobStatusReportsPayload,
  usePostAdminJobStatusReports,
} from '../../../../api-refactored/job-status-report/usePostAdminJobStatusReports';
import { useGetRecordStatus } from '../../../../api-refactored/record-status/useGetRecordStatus';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';
import DownloadFromS3Action from '../../../../components/buttons/downloadFromS3Action';
import {
  APIDropdownReturn,
  dropDownMappingToSelect,
  useAPIDropdown,
} from '../../../../hooks/dropdown';
import { customFilterChipRender } from '../../../../hooks/table';
import { JobReportFilters } from '../../../../models/JobName';
import RouteKey from '../../../../routes/key';
import JobStatusReportEmailModal from './JobStatusReportEmailModal';
import JobStatusReportResetRetry from './JobStatusReportResetRetry';
import { useJobStatusDownloadAction } from '../../logic/useJobStatusReportDownloadAction';
import DownloadAuditProgressModal from '../../../../components/modals/DownloadAuditProgressModal';

const { admin } = RouteKey.child;
const { jobStatusReport, viewRecordDetails } = RouteKey.admin.child;

const useStyles = makeStyles()((theme) =>
({
  detailSection: {
    marginBottom: theme.spacing(10),
  },
  searchBar: {
    marginBottom: theme.spacing(4.125),
  },
  datatable: {},
  maxAttempts: {
    marginTop: theme.spacing(1),
    color: theme.color.text.textTertiary,
  },
  select: {
    marginBottom: theme.spacing(3),
    width: '100%'
  },
  subtitle: {
    color: theme.color.text.textTertiary,
  },
  checkbox: {},
  actionButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface TableColumnProps {
  state: TableState;
  tableDispatch: React.Dispatch<TableActionTypes>;
  classes: Record<string,string>;
  t: TFunction;
  examLevels: APIDropdownReturn;
  recordStatuses: APIDropdownReturn;
  handleViewDetails: (uuid: string) => void;
  setSelectedEmailUuid: (uuid: string) => void;
  openEmailModal: (uuid: string) => void;
  hasEditPermission?: boolean;
}

const customFilterOptions = ({
  apiDropdownReturn,
  tableDispatch,
  state,
}: {
  apiDropdownReturn: APIDropdownReturn;
  state: TableState;
  tableDispatch: React.Dispatch<TableActionTypes>;
}) => ({
  render: (v: (string | number)[]) => [apiDropdownReturn.typeMapping[v[0]].children],
  update: (filterList: string[][],
           filterPos: number,
           index: number) => {
    tableDispatch({
      type: UPDATE_FILTERS,
      payload: {
        ...state.filters,
        requestType: state.filters.requestType,
      },
    });

    // update table inner filter
    return customFilterChipRender(filterList, filterPos, index);
  },
});

const multipleTextColumn = (textList: string[], classes: Record<string,string>) => {
  if (!textList.length) {
    return null;
  }

  const [first, ...others] = textList;

  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="body2">{first}</Typography>
      {!!others.length &&
        others.map((info) => (
          <Typography key={info} variant="subtitle2" className={classes.subtitle}>
            {info}
          </Typography>
        ))}
    </Box>
  );
};

const tableColumns = ({
  state,
  tableDispatch,
  t,
  classes,
  examLevels,
  recordStatuses,
  handleViewDetails,
  setSelectedEmailUuid,
  openEmailModal,
  hasEditPermission,
}: TableColumnProps): MUIDataTableColumnDef[] => [
    {
      name: 'userInfo',
      label: t('admin->>JobStatusReport.ColumnIdentifier'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => {
          const { candidateInfo } = state.data[dataIndex] as JobStatusReportsContent;
          return multipleTextColumn(candidateInfo, classes);
        },
      },
    },

    {
      name: 'examLevel',
      label: t('admin->>JobStatusReport.ColumnExamLevel'),
      options: {
        setCellProps: () => ({ style: { minWidth: '150px' } }),
        sort: false,
        filter: true,
        filterType: 'custom',
        customBodyRenderLite: (dataIndex: number) => {
          const { examLevel } = state.data[dataIndex] as JobStatusReportsContent;
          return <Typography variant="body2">{examLevel}</Typography>;
        },
        filterList: state.filters.examLevel,
        customFilterListOptions: customFilterOptions({
          apiDropdownReturn: examLevels,
          tableDispatch,
          state,
        }),
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => {
            const allSelected = examLevels.functionCode.length === filterList[index].length;
            return (
              <CustomAutocomplete
                className={classes.select}
                multiple
                id="inputSelect-levelOptions"
                value={filterList[index] as any}
                disableCloseOnSelect
                label={t('admin->>JobStatusReport.ExamLevelSelectLabel')}
                placeholder={t('admin->>JobStatusReport.ExamLevelPlaceholder')}
                options={examLevels.functionCode as DropDownItem[]}
                filterOptions={(options, params) =>
                  filterOptionsDropDownWithAll(options, params, 'All levels')
                }
                getOptionLabel={(option) => option.children}
                isOptionEqualToValue={(option, v) => option.key === v.key}
                renderOption={(_, option, params, __) =>
                  renderDropDownOptionWithAll(option, params, allSelected)
                }
                renderTags={renderDropDownTags}
                onChange={(_event: any, newValue, reason) => {
                  if (reason === 'selectOption' || reason === 'removeOption') {
                    if (newValue.find((option) => option.key === 'ALL')) {
                      // "ALL" option select/deselect will trigger
                      onChange(!allSelected ? examLevels.functionCode : [], index, column);
                    } else {
                      onChange(newValue, index, column);
                    }
                  }

                  if (reason === 'clear') {
                    onChange([], index, column);
                  }

                  window.dispatchEvent(new CustomEvent('resize'));
                }}
                disableListWrap
                slotProps={{
                  listbox: {
                    component: ListboxComponent as React.ComponentType<React.HTMLAttributes<HTMLElement>>
                  }
                }
                }
              />
            );
          },
        },
      },
    },
    {
      name: 'recordInfo',
      label: t('admin->>JobStatusReport.ColumnApplicationId'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => {
          const { recordInfo } = state.data[dataIndex] as JobStatusReportsContent;
          return multipleTextColumn(recordInfo, classes);
        },
      },
    },
    {
      name: 'syncAttemptDate',
      label: t('admin->>JobStatusReport.ColumnSyncAttemptDate'),
      options: {
        setCellProps: () => ({ style: { minWidth: '180px' } }),
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) =>
          stringToLongDateAndShortTime(state.data[dataIndex].syncAttemptDate),
      },
    },
    {
      name: 'recordStatus',
      label: t('admin->>JobStatusReport.ColumnStatus'),
      options: {
        setCellProps: () => ({ style: { minWidth: '160px' } }),
        sort: false,
        filter: true,
        filterType: 'custom',
        customFilterListOptions: customFilterOptions({
          apiDropdownReturn: recordStatuses,
          tableDispatch,
          state,
        }),
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <CustomSelect
              key={`status-filter-${filterList[index][0]}`}
              className={classes.select}
              label={t('admin->>JobStatusReport.ColumnStatus')}
              labelId="status-filter"
              menuItems={recordStatuses.functionCode as DropDownItem[]}
              onChange={(e) => {
                // eslint-disable-next-line no-param-reassign
                filterList[index][0] = e.target.value as string;
                onChange(filterList[index], index, column);
              }}
              placeholder={t('admin->>JobStatusReport.StatusPlaceholder')}
              displayEmpty
              // eslint-disable-next-line react/destructuring-assignment
              value={filterList[index][0] || state.filters.recordStatus?.[0]}
            />
          ),
        },
        customBodyRenderLite: (dataIndex: number) => {
          const {
            recordStatus: { code, description },
            noOfAttempts,
          } = state.data[dataIndex];

          if (code === 'SUCCESS') {
            return <Pill variant="positive" label={description} />;
          }
          if (code === 'FAILED') {
            return (
              <Box display="flex" flexDirection="column" alignItems="flex-start">
                <Pill variant="negative" label={description} />
                {noOfAttempts && (
                  <Typography variant="subtitle2" className={classes.maxAttempts}>
                    {t('admin->>JobStatusReport.MaxAttemptsCaption', { noOfAttempts })}
                  </Typography>
                )}
              </Box>
            );
          }
          return <Pill variant="info" label={description} />;
        },
      },
    },
    {
      name: 'email',
      label: t('admin->>JobStatusReport.ColumnPayload'),
      options: {
        setCellProps: () => ({ style: { padding: 0, verticalAlign: 'middle', minWidth: '150px' } }),
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <ApiButton
            color="primary"
            size="small"
            variant="text"
            onClick={() => openEmailModal(state.data[dataIndex].uuid)}
            status="idle"
            disabled={!hasEditPermission}
          >
            {t('admin->>JobStatusReport.SendEmailLabel')}
          </ApiButton>
        ),
      },
    },
    {
      name: 'actions',
      label: t('admin->>JobStatusReport.ColumnActions'),
      options: {
        sort: false,
        filter: false,
        setCellProps: () => ({ style: { padding: 0, verticalAlign: 'middle', minWidth: '150px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={classes.actionButtonsContainer}>
            {state.data[dataIndex].retryable && (
              <JobStatusReportResetRetry
                label={t('admin->>JobStatusReport.ResetRetryLabel')}
                uuids={[state.data[dataIndex].uuid]}
              />
            )}
            <ApiButton
              color="primary"
              size="small"
              variant="text"
              onClick={() => handleViewDetails(state.data[dataIndex].uuid)}
              status="idle"
            >
              {t('admin->>JobStatusReport.ViewDetailsLabel')}
            </ApiButton>
          </Box>
        ),
      },
    },
  ];

const useRawFilters = () => {
  const examLevelsResponse = useGetExamLevel({});

  const examLevels = useAPIDropdown(
    () => ({ data: examLevelsResponse.data }),
    true,
    dropDownMappingToSelect
  );

  const recordStatusesResponse = useGetRecordStatus({});

  const recordStatuses = useAPIDropdown(
    () => ({ data: recordStatusesResponse.data }),
    true,
    dropDownMappingToSelect
  );

  return { examLevels, recordStatuses };
};

interface JobStatusReportListDetailProps {
  state: TableState;
  tableDispatch: React.Dispatch<TableActionTypes>;
  tOptions: MUIDataTableOptions;
  selectionToolbar: (
    bulkActions: React.ReactNode[] | undefined,
    onClearSelection?: (() => void) | undefined
  ) => JSX.Element;
  jobFilters: JobReportFilters;
}

const JobStatusReportListDetail = ({
  state,
  tableDispatch,
  tOptions,
  selectionToolbar,
  jobFilters,
}: JobStatusReportListDetailProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const viewingState = useRef({});
  const [emailUuid, setSelectedEmailUuid] = useState('');
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [jobDescription, setJobDescription] = useState('');

  const { mutate: listMutate, isLoading: isFetching } = usePostAdminJobStatusReports({
    onSuccess: ({ result, jobName }) => {
      if (jobName.description) {
        setJobDescription(jobName.description);
      }
      tableDispatch({
        type: UPDATE_DATA,
        payload: {
          data: result.content,
          count: result.totalElements,
        },
      } as TableActionTypes);
    },
  });

  const { data: authorities } = useGetUserAuthorities();

  const hasEditPermission = useMemo(
    () => authorities?.includes(AUTHORITY_NAMES.CP_ADMIN_JOB_STATUS_REPORT_EDIT),
    [authorities]
  );

  const { examLevels, recordStatuses } = useRawFilters();

  const selectedUuids = useMemo(
    () => Object.values(state.rowsSelected).map((selected) => selected.uuid),
    [state.rowsSelected]
  );

  const selectionToolBars = useMemo(
    () => [
      <JobStatusReportResetRetry
        key="reset-retry-selected"
        label={t('admin->>JobStatusReport.ResetRetrySelected')}
        uuids={selectedUuids}
      />,
    ],
    [selectedUuids, t]
  );

  const handleViewDetails = useCallback(
    (uuid: string) => {
      history.push(
        `/${admin}/${jobStatusReport.route}/${viewRecordDetails.route}/${uuid}`,
        viewingState.current
      );
    },
    [history]
  );

  const isRowSelectable = useCallback((dataIndex: number) => state.data[dataIndex]?.retryable, [
    state.data,
  ]);

  const closeEmailModal = useCallback(() => {
    setIsEmailModalOpen(false);
  }, []);

  const openEmailModal = useCallback((uuid: string) => {
    setSelectedEmailUuid(uuid);
    setIsEmailModalOpen(true);
  }, []);

  const {
    dialogState,
    fileDownloadStatus,
    fileUuid,
    onHandleExport,
    onDialogClose,
  } = useJobStatusDownloadAction(
    jobFilters.dates.startDate,
    jobFilters.dates.endDate,
    jobFilters.jobKey,
    state.search,
    state.filters?.recordStatus,
    state.filters?.examLevel
  );

  useEffect(() => {
    const payload: JobStatusReportsPayload = {
      apiPathParams: {
        page: state.page,
        pageSize: state.size,
      },
      q: state.search,
      jobName: { uuid: jobFilters.jobKey },
      startDate: jobFilters.dates.startDate,
      endDate: jobFilters.dates.endDate,
    };

    if (state.filters?.recordStatus?.length) {
      payload.recordStatus = { code: getIndexValue(state.filters.recordStatus, 0) };
    }
    if (state.filters?.examLevel?.length) {
      payload.examLevels = state.filters.examLevel.map((item: DropDownItem) => ({
        code: item.value,
      }));
    }
    viewingState.current = {
      page: state.page,
      size: state.size,
      search: state.search,
      filters: state.filters,
      jobKey: jobFilters.jobKey,
      dates: { startDate: jobFilters.dates.startDate, endDate: jobFilters.dates.endDate },
    };
    listMutate(payload);
  }, [
    state.order,
    state.page,
    state.size,
    state.sortColumn,
    state.filters,
    state.search,
    listMutate,
    jobFilters.jobKey,
    jobFilters.dates.startDate,
    jobFilters.dates.endDate,
  ]);

  const appendedServerSizePaginationColumnOption = useMemo<MUIDataTableOptions>(() => {
    const customizeToolBar = () => {
      if (state.count === 0) {
        return (
          <Button variant="text" color="primary" disabled>
            Export
          </Button>
        );
      }
      return (
        <DownloadFromS3Action
          color="primary"
          actionType="link"
          requestRoute={{ data: fileUuid, url: '/api/s3/files' }}
          label="Export"
          onClick={onHandleExport}
          onDownload={() => null}
        />
      );
    };

    return {
      pagination: true,
      sort: false,
      filter: true,
      selectableRows: 'multiple',
      selectToolbarPlacement: 'replace',
      isRowSelectable,
      filterType: 'multiselect',
      customToolbar: customizeToolBar,
    };
  }, [fileUuid, isRowSelectable, onHandleExport, state.count]);

  return (
    <ContentTemplate transparent={false} type="full">
      <Box className={classes.detailSection}>
        <SectionHeader title={t('admin->>JobStatusReport.ListDetailTitle')} />
        <Box>
          <InputLabel>{t('admin->>JobStatusReport.JobDescriptionLabel')}</InputLabel>
          <Typography variant="body2">{jobDescription}</Typography>
        </Box>
      </Box>

      <SearchBar
        className={classes.searchBar}
        name="searchReportInput"
        ariaLabel="searchReportInput"
        placeholder={t('admin->>JobStatusReport.SearchPlaceholder')}
        onSearch={(str: string) => {
          tableDispatch({ type: UPDATE_SEARCH, payload: str });
        }}
        searchButton={!isMobile}
        clearInputButton
        defaultValue={state.search}
      />
      <Box className={classes.datatable}>
        <MUIDataTable
          state={state}
          dispatch={tableDispatch}
          isLoading={isFetching}
          columns={tableColumns({
            state,
            tableDispatch,
            t,
            classes,
            examLevels,
            recordStatuses,
            handleViewDetails,
            setSelectedEmailUuid,
            openEmailModal,
            hasEditPermission,
          })}
          data={state.data}
          emptyDataLabel={t('admin->>JobStatusReport.NoResultsMessage')}
          options={{
            ...tOptions,
            ...appendedServerSizePaginationColumnOption,
          }}
          components={{
            TableToolbarSelect: () => selectionToolbar(selectionToolBars),
          }}
          rowsPerPageOptions={[20, 30, 40, 50]}
        />
        <DownloadAuditProgressModal
          fileDownloadStatus={fileDownloadStatus}
          modalState={dialogState}
          onDialogClose={onDialogClose}
        />
      </Box>
      <JobStatusReportEmailModal
        open={isEmailModalOpen}
        uuid={emailUuid}
        closeModal={closeEmailModal}
      />
    </ContentTemplate>
  );
};

export default JobStatusReportListDetail;
